import { AbortedError, NetworkError, ResponseError, TimeoutError } from 'app/constants/ResponseErrors';
import { notify } from 'app/services/errorReporter';
import Logger from 'app/services/logger';
import { IPollInterval } from 'app/services/rtc/bookings/behavior/types';
import { createGuid } from 'app/types/guid';
import { sleep } from 'app/utils/async';
import { CancelToken } from 'app/utils/cancelToken';

interface IPollOptions {
  // Attempts to distribute load by randomizing the polling between intervals.
  distributeLoad?: boolean;
  // Random number generator used for distributing the interval.
  random?: () => number;
}

/**
 * Helps facilitate polling for bookings.
 *
 * @pollIntervals Array of poll intervals.
 * @returns An unsubscribe method.
 */
export const poll = (
  fetch: (cancelToken: CancelToken) => Promise<unknown>,
  getServerTime: () => Date,
  pollIntervals: IPollInterval[],
  options: IPollOptions = {},
) => {
  // Create an arbitrary ID just in case to distinguish multiple polling events running in unison.
  const id = createGuid();
  const {
    distributeLoad = false,
    random = Math.random,
  } = options;
  const canceller = CancelToken.create();
  const cancelToken = canceller.cancelToken;

  /**
   * Computes how long it should take to poll again, depending on the start date, end date
   * and relative time. Then queues up the operation.
   */
  const cycle = async () => {
    const currentServerTime = getServerTime();

    // If the next tick for the current interval would overlap the next interval, then the next tick becomes the
    // earlier of:
    //
    // a) The next tick for the current interval
    // b) One tick from the start of the next interval
    //
    // For example, if there is a 3ms tick duration starting at Date(0) and ending at Date(1),
    // followed by a 1ms tick duration, then the next tick event will happen at Date(2), that is, Date(1) + 1ms.
    const candidates: number[] = pollIntervals
      // Filter out intervals that could not possibly have a polling event based on the current time.
      // This creates an invariant that all intervals are current or in the future.
      .filter((interval) => {
        return interval.end.valueOf() - interval.sleepMs >= currentServerTime.valueOf();
      })
      .map((interval, i) => {
        // Determine if the user is inside this interval.
        const currentInterval = currentServerTime <= interval.end && currentServerTime >= interval.start;

        // If the user is in the current interval, simply sleep for the defined duration.
        if (currentInterval) {
          return interval.sleepMs;
        }

        // If distribute is enabled then use the random function to choose a random point in the
        // next interval.
        const factor = distributeLoad ? random() : 1;
        const randomizedSleepMs = factor * interval.sleepMs;

        // Otherwise, compute the sleep duration from the start of the polling interval.
        return (interval.start.valueOf() - currentServerTime.valueOf()) + randomizedSleepMs;
      });

    // Stop execution if there are no viable ticks left.
    if (candidates.length === 0) {
      Logger.debug('poll has ended, no more candidates.', { id });
      return;
    }

    // Find the closest candidate.
    const timeToSleep: number = candidates.reduce((a, b) => a < b ? a : b);

    Logger.debug(`poll sleeping.`, { id, timeToSleep });

    try {
      await sleep(timeToSleep, cancelToken);
      await fetch(cancelToken);
    } catch (error) {
      Logger.debug('poll error occured.', { id });

      // Ignore the block if the error is due to cancellation.
      if (error !== undefined && error !== cancelToken.getReason()) {
        const safeError =
          error instanceof NetworkError ||
          error instanceof TimeoutError ||
          error instanceof AbortedError ||
          (error instanceof ResponseError && error.status && error.status < 500);

        // Stop polling if the error is not related to network or maintainence issues.
        if (!safeError) {
          notify(error);
          return;
        }
      }
    }

    // Continue to cycle until the cycle method causes early execution or the cancel token has been executed.
    if (!cancelToken.isCancelled()) {
      cycle();
    }
  };

  Logger.debug('poll cycle starting.', { id });
  cycle();

  return () => {
    Logger.debug('poll has been unsubscribed. ', { id });
    canceller.cancel();
  };
};
